import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why CSS-in-JS is winning, an example",
  "description": "",
  "published": "2019-01-30T00:00:00.000Z",
  "redirect_from": ["/blog/css-in-js-winning-an-example/swizec/8917"],
  "categories": "Front End, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p><img parentName="p" {...{
        "src": "/c31ece0473f4122cca27955d9ec9d9eb/mCL3XaJ.gif",
        "alt": "Stepper component built with CSS-in-JS"
      }}></img></p>
    <p>{`Modern CSS-in-JS is the best thing that's happened to the web since components. Hands down. Let me explain. 👇`}</p>
    <p>{`First we had the `}<inlineCode parentName="p">{`style`}</inlineCode>{` prop and that sucked. I hated CSS-in-JS and thought it was stupid. You can pry my CSS from my dead cold hands! `}<em parentName="p">{`shakes fist`}</em></p>
    <p>{`Then came experiments.`}</p>
    <p>{`Can we make the style prop easier to use? Can we still use class names to style with BEM like we used to? What about CSS files with fat semantic classes? Scoped styles maybe?`}</p>
    <p>{`None of it quite worked. Everything was awkward in some way.`}</p>
    <p>{`The style prop is great for individual components and breaks down when you want to change your general font size.`}</p>
    <p>{`Class names give you all the power of CSS and BEM (tiny classes like .font1 .bold .bla). And it breaks down when you reuse components like building blocks. A component has the color defined and if you want to overwrite that you need something that understands classes and decides which one takes power and ugh don't get me started.`}</p>
    <p>{`I may have once tried to make that work ...`}</p>
    <p>{`Scoped CSS is better. That's what Vue uses. But it still sucks, if you ask me. You get a weird mix of global styles with local overwrites and mixing BEM and semantic classes and it gets messy fast. Leads to weird code too.`}</p>
    <p>{`But then, then some beautiful soul came up with `}<a parentName="p" {...{
        "href": "https://www.styled-components.com/"
      }}>{`styled-components`}</a>{`. I was in love.`}</p>
    <p>{`All the simplicity of scoped CSS with the power of the style prop. You write CSS, yes, `}<em parentName="p">{`but you can inject JavaScript too`}</em>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "/cacb8b2018af744b78e30339b7c4868a/media-26FLdmIp6wJr91JAI-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "why-styled-components-are-win-an-example"
    }}>{`Why styled-components are win, an example`}</h2>
    <p>{`The best way to make my argument is with an example. I recently implemented a colorful React stepper component for a client project.`}</p>
    <p>{`Styled components and a couple libraries `}<a parentName="p" {...{
        "href": "https://twitter.com/diegohaz"
      }}>{`@Diegohaz`}</a>{` built on top made it so easy it feels like cheating.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Wow `}<a parentName="p" {...{
          "href": "https://twitter.com/diegohaz"
        }}>{`@diegohaz`}</a>{`'s libs made this ridiculously easy to build. Blog coming soon `}<a parentName="p" {...{
          "href": "https://t.co/Cpp7LoMrHC"
        }}>{`pic.twitter.com/Cpp7LoMrHC`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1090146315361636352"
      }}>{`January 29, 2019`}</a></blockquote>
    <p>{`You have 9 steps. Each a different color once done. Blank with a grey outline otherwise.`}</p>
    <p>{`Here's a CodeSandbox you can try.`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/vy3ll0220",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`Pretty isn't it? I think it is.`}</p>
    <p>{`Here's all the component code 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`<code lang="javascript">const Step = ({ index, done, last }) => (
  <Circle index={index} done={done} last={last}>
    {last ? "⭐️" : index + 1}
  </Circle>
);

export default ({ step, maxSteps }) => (
  <Steps row>
    {range(maxSteps).map(i => (
      <Step index={i} key={i} last={i === maxSteps - 1} done={i < step} />
    ))}
  </Steps>
);
`}</code></pre>
    <p>{`Yep that's it. 12 lines of code.`}</p>
    <p>{`A `}<inlineCode parentName="p">{`<Stepper>`}</inlineCode>{` component, the default export, takes the current `}<inlineCode parentName="p">{`step`}</inlineCode>{` and max number of steps, `}<inlineCode parentName="p">{`maxSteps`}</inlineCode>{`. It renders a `}<inlineCode parentName="p">{`<Steps>`}</inlineCode>{` component which deals with layouting. Inside goes an array of `}<inlineCode parentName="p">{`<Step>`}</inlineCode>{`s.`}</p>
    <p>{`Each `}<inlineCode parentName="p">{`<Step>`}</inlineCode>{` gets an index, a flag saying whether it's `}<inlineCode parentName="p">{`last`}</inlineCode>{`, and whether it's been `}<inlineCode parentName="p">{`done`}</inlineCode>{` yet. Highlighted vs. unhighlighted state.`}</p>
    <p>{`Steps render a `}<inlineCode parentName="p">{`<Circle>`}</inlineCode>{` component, pass-in the props, and inside goes either a number or a star emoji. Star is last.`}</p>
    <h2 {...{
      "id": "all-style-logic-goes-in-styles"
    }}>{`All style logic goes in styles`}</h2>
    <p>{`Moving your `}<em parentName="p">{`visual`}</em>{` UI logic out of your components and into styles is where CSS-in-JS shines. Focus your components on business logic, user experience, and hierarchy. Let your styles take care of your styling.`}</p>
    <p>{`Just like our `}<a parentName="p" {...{
        "href": "http://www.csszengarden.com/"
      }}>{`CSS Garden`}</a>{` forebears envisioned. Remember CSS Gardens?`}</p>
    <p>{`You might be too young. The world has moved on. CSS's initial promise is that we would completely decouple styling from document hierarchy and live a Zen stress free life.`}</p>
    <p>{`Now look at us.`}</p>
    <p>{`BUT ALL IS NOT LOST! Look at this beauty.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">import { Box, Flex, Heading, styled } from "reakit";

const Steps = styled(Flex)\`
    align-items: center;
    justify-content: center;
    padding-top: 40px;
\`;
`}</code></pre>
    <p>{`I'm using Diegohaz's `}<a parentName="p" {...{
        "href": "https://reakit.io/"
      }}>{`reakit`}</a>{` for base CSS stuff. He built a bunch of components on top of styled-components that have sane defaults and great composability.`}</p>
    <p>{`No styles, just some basics. `}<inlineCode parentName="p">{`Flex`}</inlineCode>{`, for example, is a `}<inlineCode parentName="p">{`div`}</inlineCode>{` with `}<inlineCode parentName="p">{`display: flex`}</inlineCode>{`. That's all.`}</p>
    <p><inlineCode parentName="p">{`<Steps>`}</inlineCode>{` then is a flexbox div that centers its children and adds a `}<inlineCode parentName="p">{`40px`}</inlineCode>{` padding on top.`}</p>
    <p>{`Easy 👌`}</p>
    <p>{`And then comes the `}<inlineCode parentName="p">{`<Circle>`}</inlineCode>{` component. The individual steps.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">import { theme, palette, withProp, ifProp } from "styled-tools";

const Circle = styled(Box)\`
    border-radius: 100%;
    border: 1px solid;
    border-color: \${ifProp(
        "done",
        withProp("index", index => palette("steps", index)),
        palette("grey", 2)
    )}
    color: \${ifProp("done", "white", palette("grey", 2))};
    background: \${ifProp(
        "done",
        withProp("index", index => palette("steps", index)),
        "white"
    )}
    font-size: \${palette("size", 1)};
    \${theme("bold")}
    width: 30px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    margin-right: 20px;

    &:last-child {
        margin-right: 0px;
    }
\`;
`}</code></pre>
    <p><img parentName="p" {...{
        "src": "/fe91a3dc38b1981226c5c025fc759674/media-xT0xeJpnrWC4XWblEk-giphy.gif",
        "alt": null
      }}></img></p>
    <p>{`A few things are happening here. Let me explain.`}</p>
    <ol>
      <li parentName="ol">{`I'm using another of Diegohaz's libraries `}<a parentName="li" {...{
          "href": "https://github.com/diegohaz/styled-tools"
        }}>{`styled-tools`}</a>{`. It's a suite of helper functions that make it easier to use component props from your CSS-in-JS styles`}</li>
      <li parentName="ol">{`I'm using a `}<em parentName="li">{`theme`}</em>{` behind the scenes. A concept built into Reakit that lets you define global styles. Things like UI colors, font sizes, stuff like that.`}</li>
      <li parentName="ol"><strong parentName="li">{`ALL`}</strong>{` of my visual logic is in this style`}</li>
    </ol>
    <p>{`Let's break it down.`}</p>
    <p>{`We start with a `}<inlineCode parentName="p">{`Box`}</inlineCode>{`, which is just a `}<inlineCode parentName="p">{`div`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">const Circle = styled(Box)\`
`}</code></pre>
    <p>{`Then we turn it into a circle with a border.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="css">    border-radius: 100%;
    border: 1px solid;
`}</code></pre>
    <p>{`Border and background color come from our `}<inlineCode parentName="p">{`theme`}</inlineCode>{`, based on the combination of `}<inlineCode parentName="p">{`index`}</inlineCode>{` and `}<inlineCode parentName="p">{`done`}</inlineCode>{` props.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">    border-color: \${ifProp(
        "done",
        withProp("index", index => palette("steps", index)),
        palette("grey", 2)
    )}
    background: \${ifProp(
        "done",
        withProp("index", index => palette("steps", index)),
        "white"
    )}
`}</code></pre>
    <p>{`Here's how you read that:`}</p>
    <ol>
      <li parentName="ol">{`If prop `}<inlineCode parentName="li">{`done`}</inlineCode>{` is true, get first argument, otherwise the second.`}</li>
      <li parentName="ol">{`With prop `}<inlineCode parentName="li">{`index`}</inlineCode>{` run method.`}</li>
      <li parentName="ol">{`Get the `}<inlineCode parentName="li">{`steps`}</inlineCode>{` palette from theme and take the `}<inlineCode parentName="li">{`index`}</inlineCode>{` color.`}</li>
      <li parentName="ol">{`Otherwise, get the `}<inlineCode parentName="li">{`grey`}</inlineCode>{` palette and take the `}<inlineCode parentName="li">{`2`}</inlineCode>{`nd color`}</li>
    </ol>
    <p>{`Palettes are arrays in your theme object. Pure JavaScript. You can see it in the CodeSandbox above.`}</p>
    <p>{`A similar process gives you the font color.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">    color: \${ifProp("done", "white", palette("grey", 2))};
`}</code></pre>
    <p>{`If step is `}<inlineCode parentName="p">{`done`}</inlineCode>{`, color is `}<inlineCode parentName="p">{`white`}</inlineCode>{`, otherwise the 2nd color from the `}<inlineCode parentName="p">{`grey`}</inlineCode>{` palette.`}</p>
    <p>{`Then we have font size, bold fonts, widths and heights, and other normal CSS stuff to round off each step.`}</p>
    <pre><code parentName="pre" {...{}}>{`<code class="javascript">    font-size: \${palette("size", 1)};
    \${theme("bold")}
    width: 30px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    margin-right: 20px;

    &:last-child {
        margin-right: 0px;
    }
\`;
`}</code></pre>
    <p>{`You can still use CSS nesting and meta classes just like you're used to. `}<inlineCode parentName="p">{`&:last-child`}</inlineCode>{` matches the last `}<inlineCode parentName="p">{`<Step>`}</inlineCode>{` and removes its margin so it doesn't mess with the layout.`}</p>
    <p>{`👌`}</p>
    <h2 {...{
      "id": "conclusion"
    }}>{`Conclusion`}</h2>
    <p>{`This flavor of CSS-in-JS is `}<em parentName="p">{`the`}</em>{` best most ridiculously easy way I've ever seen to style modern JavaScript components. I pray that it works for Vue as well as it does for React because I want to get my frontend peeps at the dayjob excited about it.`}</p>
    <p>{`To recap:`}</p>
    <ol>
      <li parentName="ol">{`Components deal with business logic and UX`}</li>
      <li parentName="ol">{`Styles handle all visual logic`}</li>
      <li parentName="ol">{`Global theme handles generics`}</li>
    </ol>
    <p>{`Yes that means you ca rebrand your whole app just by changing `}<inlineCode parentName="p">{`theme.js`}</inlineCode>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "/d3a0d92f6945f05f367f18a4d5bbe936/media-l0MYw6Cu1TfY3gsWk-giphy.gif",
        "alt": null
      }}></img></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      